import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Users, Plus, Edit3, Trash2, Shield, User, Mail, Phone, Calendar, Search, Filter } from 'lucide-react'
import LoadingSpinner from '../LoadingSpinner'
import { adminService } from '../../services/api'

const UsersTab = ({ 
  users: propUsers,
  usersLoading: propUsersLoading,
  onAddUser, 
  onEditUser, 
  onDeleteUser
}) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')

  // Use props if available, otherwise fetch data ourselves
  const shouldFetchData = !propUsers && !propUsersLoading
  
  // Fetch users using React Query only if not provided via props
  const { data: usersResponse, isLoading: usersLoading, error: usersError, refetch: fetchUsers } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminService.getUsers(),
    retry: false,
    enabled: shouldFetchData,
  })

  const deleteUserMutation = useMutation({
    mutationFn: (userId) => adminService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users'])
    }
  })

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, userData }) => adminService.updateUser(userId, userData),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users'])
    }
  })

  // Use prop data if available, otherwise use fetched data
  const users = propUsers || usersResponse?.users || []
  const loading = propUsersLoading !== undefined ? propUsersLoading : usersLoading

  const handleAddUser = () => {
    if (onAddUser) {
      onAddUser()
    } else {
      navigate('/admin/users/new')
    }
  }

  const handleEditUser = (user) => {
    if (onEditUser) {
      onEditUser(user)
    } else {
      navigate(`/admin/users/edit/${user.id}`)
    }
  }

  const handleDeleteUser = async (user) => {
    if (!user || !user.id) {
      console.error('Invalid user object:', user)
      return
    }
    
    if (window.confirm(`هل أنت متأكد من حذف المستخدم ${user.name || user.email}؟`)) {
      if (onDeleteUser) {
        onDeleteUser(user.id)
      } else {
        deleteUserMutation.mutate(user.id)
      }
    }
  }

  const handleToggleUserStatus = async (user) => {
    // Since we removed is_active, this function is disabled
    console.log('User status toggle disabled - is_active column removed')
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Shield className="h-4 w-4 text-red-600" />
      case 'customer':
        return <User className="h-4 w-4 text-blue-600" />
      default:
        return <User className="h-4 w-4 text-gray-600" />
    }
  }

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-red-100 text-red-800',
      customer: 'bg-blue-100 text-blue-800',
      moderator: 'bg-purple-100 text-purple-800'
    }
    
    const roleNames = {
      admin: 'مدير',
      customer: 'عميل',
      moderator: 'مشرف'
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100 text-gray-800'}`}>
        {getRoleIcon(role)}
        <span className="ml-1">{roleNames[role] || role}</span>
      </span>
    )
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Filter users based on search and filters
  const filteredUsers = users?.filter(user => {
    const matchesSearch = !searchTerm || 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesRole = !roleFilter || user.role === roleFilter

    return matchesSearch && matchesRole
  }) || []

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">إدارة المستخدمين</h2>
        <button 
          onClick={handleAddUser}
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          إضافة مستخدم
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="البحث في المستخدمين..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div className="sm:w-48">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="input"
              >
                <option value="">جميع الأدوار</option>
                <option value="admin">مدير</option>
                <option value="customer">عميل</option>
                <option value="moderator">مشرف</option>
              </select>
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الاسم
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      معلومات التواصل
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      الدور
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      تاريخ الانضمام
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      الإجراءات
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-500" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name || 'غير محدد'}
                            </div>
                            <div className="text-sm text-gray-500">ID: {user.id}</div>
                            {/* Mobile-only additional info - simplified */}
                            <div className="md:hidden text-xs text-gray-500 mt-1">
                              <div>{user.email}</div>
                              <div>{getRoleBadge(user.role)}</div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            {user.phone}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        {getRoleBadge(user.role)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2" />
                          {formatDate(user.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col space-y-1 md:flex-row md:space-y-0 md:space-x-2">
                          <button 
                            onClick={() => handleEditUser(user)}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50 flex items-center justify-center"
                            title="تعديل المستخدم"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 flex items-center justify-center"
                            title="حذف المستخدم"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {searchTerm || roleFilter ? 'لم يتم العثور على مستخدمين' : 'لا يوجد مستخدمون بعد'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || roleFilter
                  ? 'جرب تعديل معايير البحث أو المرشحات.'
                  : 'ابدأ بإنشاء أول حساب مستخدم.'
                }
              </p>
              {!searchTerm && !roleFilter && (
                <button 
                  onClick={handleAddUser}
                  className="mt-4 btn btn-primary"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  إضافة المستخدم الأول
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Users Stats */}
      {users && users.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white shadow rounded-lg border p-4">
            <div className="flex items-center">
              <Users className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">إجمالي المستخدمين</p>
                <p className="text-2xl font-semibold text-gray-900">{users.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg border p-4">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">المديرون</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white shadow rounded-lg border p-4">
            <div className="flex items-center">
              <User className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">العملاء</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {users.filter(u => u.role === 'customer').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UsersTab
